import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API, fileUrl } from "../lib/api";
import CanvasElement from "../components/editor/CanvasElement";
import { loadDesignFonts } from "../lib/fontLoader";
import { PenNib, ArrowRight, Play, Pause, MusicNotes } from "@phosphor-icons/react";

export default function ShareView() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [design, setDesign] = useState(null);
  const [error, setError] = useState(false);
  const [scale, setScale] = useState(0.4);
  const wrapRef = useRef(null);
  const audioRef = useRef(null);
  const [audioPlaying, setAudioPlaying] = useState(false);

  const hasAudio = design?.audio && design.audio.storage_path;

  const toggleAudio = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) { a.play().then(() => setAudioPlaying(true)).catch(() => {}); }
    else { a.pause(); setAudioPlaying(false); }
  };

  useEffect(() => {
    if (audioRef.current && hasAudio) audioRef.current.volume = design.audio.volume ?? 1;
  }, [hasAudio, design]);

  useEffect(() => {
    loadDesignFonts();
    const load = async () => {
      try {
        const res = await api.get(`/share/${shareId}`);
        // Register any custom fonts bundled with the design
        (res.data.fonts || []).forEach((f) => {
          try {
            const ff = new FontFace(f.family, `url("${API}/fonts/file/${f.storage_path}")`);
            ff.load().then((loaded) => document.fonts.add(loaded)).catch(() => {});
          } catch (e) { console.warn("Custom font load failed:", f.family, e); }
        });
        setDesign(res.data);
      } catch (e) {
        console.error("Failed to load shared design:", e);
        setError(true);
      }
    };
    load();
  }, [shareId]);

  const compute = useCallback(() => {
    if (!wrapRef.current || !design) return;
    const w = wrapRef.current.clientWidth - 32;
    setScale(Math.min(w / design.width, 700 / design.height));
  }, [design]);

  useEffect(() => {
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [compute]);

  const visibleElements = useMemo(
    () => (design ? [...design.elements].filter((el) => !el.hidden).sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0)) : []),
    [design]
  );

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#0A0A0A] text-white gap-4 px-6 text-center">
        <h1 className="font-display text-2xl font-bold">This design isn't available</h1>
        <p className="text-zinc-400">The link may be private or no longer exists.</p>
        <button onClick={() => navigate("/")} className="px-5 py-2.5 rounded-full bg-blue-600 hover:bg-blue-500 text-sm font-semibold">Go to FlyerStudio</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-body flex flex-col">
      <header className="h-16 border-b border-white/10 flex items-center justify-between px-6 shrink-0">
        <button onClick={() => navigate("/")} className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-blue-600 flex items-center justify-center"><PenNib weight="fill" size={18} /></div>
          <span className="font-display font-extrabold text-lg tracking-tight">FlyerStudio</span>
        </button>
        <button onClick={() => navigate("/")} data-testid="share-cta" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white text-black text-sm font-semibold hover:bg-zinc-200 transition-colors">
          Create your own <ArrowRight weight="bold" size={15} />
        </button>
      </header>

      <div ref={wrapRef} className="flex-1 flex flex-col items-center justify-center p-6">
        {design && (
          <>
            <h1 className="font-display text-xl font-bold mb-5 text-center">{design.title}</h1>
            <div
              style={{ width: design.width * scale, height: design.height * scale, background: design.background, position: "relative", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.55)" }}
              data-testid="share-artboard"
            >
              {visibleElements.map((el) => (
                <CanvasElement key={el.id} el={el} scale={scale} interactive={false} isSelected={false} />
              ))}
            </div>
            {hasAudio && (
              <>
                <audio
                  ref={audioRef}
                  src={fileUrl(design.audio.storage_path)}
                  loop={design.audio.loop !== false}
                  onEnded={() => setAudioPlaying(false)}
                  crossOrigin="anonymous"
                />
                <button
                  onClick={toggleAudio}
                  data-testid="share-audio-btn"
                  className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-blue-600 hover:bg-blue-500 text-sm font-semibold transition-colors"
                >
                  {audioPlaying ? <Pause weight="fill" size={16} /> : <Play weight="fill" size={16} />}
                  <MusicNotes weight="fill" size={15} /> {audioPlaying ? "Pause music" : "Play with music"}
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
